import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { useAbortController } from "src/hooks";
import { DEFAULT_TRACKER_PERSPECTIVE } from "src/constants";
import { getSuggestedTrackers } from "src/store/trackers/trackersApi";
import {
  selectLanguagesEntities,
  selectLocationsEntities,
} from "src/store/selectors";
import {
  showToastNotification,
  showDevelopmentError,
  isAbortedRequestErrorTypeGuard,
} from "src/utils";

const SUGGESTED_TRACKERS_LIMIT_PER_REQUEST: number = 6;

type Props = {
  updateStatusHandler: (status: LoadingStatus) => void;
  updateExcludedTrackersHandler: (trackers: Tracker.CreationData[]) => void;
};

export const useGetSuggestedTrackers = ({
  updateStatusHandler,
  updateExcludedTrackersHandler,
}: Props) => {
  const { t } = useTranslation();

  const abortController = useAbortController();

  const locations = useSelector(selectLocationsEntities);

  const languages = useSelector(selectLanguagesEntities);

  const getExcludedTrackers = useCallback(
    (trackers: Tracker.CreationData[]): string[] => {
      const trackerNames = trackers.map(({ name }) => name);

      return Array.from(new Set(trackerNames));
    },
    [],
  );

  const getLocation = useCallback(
    (locationId: Location.Data["id"]): Location.Data | undefined =>
      locations[locationId],
    [locations],
  );

  const getLanguage = useCallback(
    (languageId: Language.Data["id"]): Language.Data | undefined =>
      languages[languageId],
    [languages],
  );

  const _getSuggestedTrackers = async ({
    category,
    callback,
    locationId,
    languageId,
    keywordsDataSource,
    excludedTrackers = [],
    perspective = DEFAULT_TRACKER_PERSPECTIVE,
    limit = SUGGESTED_TRACKERS_LIMIT_PER_REQUEST,
  }: {
    limit?: number;
    category: Tracker.Category;
    callback?: (trackers: Tracker.CreationData[]) => void;
    locationId: Location.Data["id"];
    languageId: Language.Data["id"];
    perspective?: Tracker.Perspective;
    excludedTrackers?: Tracker.CreationData[];
    keywordsDataSource: Search.KeywordsDataSource;
  }): Promise<Tracker.CreationData[]> => {
    const [location, language] = [
      getLocation(locationId),
      getLanguage(languageId),
    ];

    if (!location || !language) return [];

    try {
      updateStatusHandler("loading");

      const result = await getSuggestedTrackers(
        {
          limit,
          location,
          language,
          category,
          perspective,
          keywordsDataSource,
          excludedSubjects: getExcludedTrackers(excludedTrackers),
        },
        { signal: abortController?.signal },
      );

      updateExcludedTrackersHandler([...excludedTrackers, ...result]);

      callback?.(result);

      updateStatusHandler("succeeded");

      return result;
    } catch (error) {
      if (isAbortedRequestErrorTypeGuard(error)) return [];

      showDevelopmentError({ error });

      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });

      callback?.([]);

      updateStatusHandler("failed");

      return [];
    }
  };

  const cancelGetSuggestedTrackers = (): void => {
    if (abortController.signal.aborted) return;

    return abortController.abort();
  };

  return {
    cancelGetSuggestedTrackers,
    getSuggestedTrackers: _getSuggestedTrackers,
  };
};
